import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GlobalComponentService, ReusableInfoAlertComponent } from '@techmore/global';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, Subject, Subscription, throwError, timer } from 'rxjs';
import { delay, filter, tap } from 'rxjs/operators';
import { OAuthError } from 'src/app/share/entity/OauthError';
import { ServerConfigService } from 'src/app/share/service/server-config.service';
import { parseJWTtoken } from 'src/app/util';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../tokens/generator-env.token';
import { AbstractTemplateService } from '../../web-generator/template-generator/entity/abstract-template-service';
import { BackStagePayload } from '../../web-generator/template-generator/entity/back-stage-payload';
import { ITemplate } from '../../web-generator/template-generator/entity/i-template';
import { TemplateType } from '../entity/TemplateType';

/** 後台模板編輯服務代理 */
@Injectable({
  providedIn: 'root',
})
export class BackStageTemplateService {
  _isRefreshing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** 模板服務 */
  templateService: AbstractTemplateService<ITemplate>;
  /** 模板類型 */
  type: TemplateType;
  /** 最後一次操作畫面的時間戳記 */
  private lastedActiveTimeStamp: number;
  /** 超時警告計時器訂閱 */
  private timeoutAlertTimerSubc: Subscription;
  /** 超時警告計時器訂閱 */
  private timeoutAlert2TimerSubc: Subscription;
  /** 超時計時器訂閱 */
  private timeoutTimerSubc: Subscription;

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) private generatorEnv: GeneratorEnv,
    private route: ActivatedRoute,
    private globalComponentService: GlobalComponentService,
    private translateService: TranslateService,
    private serverConfigService: ServerConfigService,
  ) {}

  listenOnWindowMessage(isIndexPage: boolean, isSubjectDetailPage: boolean): Subject<BackStagePayload> {
    this.type = this.getTemplateType(isIndexPage, isSubjectDetailPage);

    const subj = new Subject<BackStagePayload>();
    window.addEventListener('message', (event) => {
      const match = this.generatorEnv.allowBackStageDomain.find(domain => domain === event.origin);
      if (!match) { return; }

      const dataFromParent = event.data;
      if (dataFromParent) {
        if (this.type === TemplateType.index) { // 首頁
          if (dataFromParent.indexEdit && dataFromParent.indexEdit.accessToken && dataFromParent.indexEdit.refreshToken) {
            // 首頁編輯
            sessionStorage.setItem(this.generatorEnv.accessTokenKey, dataFromParent.indexEdit.accessToken);
            sessionStorage.setItem(this.generatorEnv.refreshTokenKey, dataFromParent.indexEdit.refreshToken);
            this.startReadyToTimeOutTimer(dataFromParent.indexEdit.accessToken, dataFromParent.indexEdit.refreshToken);
            subj.next(dataFromParent.indexEdit);
            if (this._isRefreshing.value) { this._isRefreshing.next(false); }
            if (!this.serverConfigService.isEditModeUrl) { this.serverConfigService.isEditModeUrl = true; }
          } else if (dataFromParent === 'close') {
            window.close();
          } else if (dataFromParent === 'timeoutRefreshFail') {
            this.globalComponentService.openErrorAlert('連線超時，請重新登入', {
              afterClose: () => {
                window.close();
              }
            });
          }
        } else if (this.type === TemplateType.subject) { // 主題頁
          if (dataFromParent.subjectEdit && dataFromParent.subjectEdit.accessToken) {
            // 主題編輯
            sessionStorage.setItem(this.generatorEnv.accessTokenKey, dataFromParent.subjectEdit.accessToken);
            sessionStorage.setItem(this.generatorEnv.refreshTokenKey, dataFromParent.subjectEdit.refreshToken);
            this.startReadyToTimeOutTimer(dataFromParent.subjectEdit.accessToken, dataFromParent.subjectEdit.refreshToken);
            subj.next(dataFromParent.subjectEdit);

            if (this._isRefreshing.value) {
              this._isRefreshing.next(false);
            } else {
              if (!this.serverConfigService.isEditModeUrl) { this.serverConfigService.isEditModeUrl = true; }
            }
          } else if (dataFromParent === 'close') {
            window.close();
          } else if (dataFromParent === 'timeoutRefreshFail') {
            this.globalComponentService.openErrorAlert('連線超時，請重新登入', {
              afterClose: () => {
                window.close();
              }
            });
          }
        } else { // 未定意的位址
          console.error('未匹配路由的網址');
        }
      }
    }, false);
    return subj;
  }

  private getTemplateType(isIndexPage: boolean, isSubjectDetailPage: boolean) {
    if (isIndexPage) {
      return TemplateType.index;
    }
    if (isSubjectDetailPage) {
      return TemplateType.subject;
    }
    throw new Error('未匹配到樣板類型');
  }

  /** 註冊模板服務實例 */
  registTemplateService(service: AbstractTemplateService<ITemplate>, type: TemplateType) {
    if (type === this.type) {
      this.templateService = service;
      service._errorHandler = this._errorHandler;
    }
  }

  // /** 提交首頁編輯模板設定 */
  // updateTemplate(): Observable<Template> {
  //   return this.templateService.updateTemplate().pipe(
  //     catchError((error, observable) => this._errorHandler<any>(error, observable)),
  //   );
  // }

  // /** 移動區塊順序 */
  // moveSection(uuid: string, direction: boolean) {
  //   this.templateService.moveSection(uuid, direction);
  // }

  private _errorHandler = <T extends unknown>(error: HttpErrorResponse, observableFunc: (args?: any) => Observable<T>): Observable<any> => {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        switch (error.error.subStatus) {
          case '612':
            // FIXME: 登入超時
            if (!this._isRefreshing.getValue()) {
              this._isRefreshing.next(true);
              this.requestUpdateToken();
            }
            const subj = new Subject<T>();
            this._isRefreshing.pipe(
              filter(r => !r),
              // switchMap(r => observableFunc().pipe(delay(500))),
              tap(v => {
                // console.log('123');
              })
            ).subscribe(v => {
              observableFunc().pipe(delay(500)).subscribe(v2 => {
                subj.next(v2);
                // subj.complete();
              });
            });
            return subj;
          case '611':
            // 認證失效 請重新登入
            const oauthError: OAuthError = Object.assign(new OAuthError(this.translateService), error.error);
            window['tokenTimeout'] = true;
            this.globalComponentService.openErrorAlert(
              oauthError.getMessage(),
              {
                afterClose: () => {
                  this.requestUpdateToken();
                  window.close();
                },
                showCopyTip: true
              }
            );
            break;
          case '610':
            // token 未經授權
            const oauthError2: OAuthError = Object.assign(new OAuthError(this.translateService), error.error);
            window['tokenTimeout'] = true;
            this.globalComponentService.openErrorAlert(
              oauthError2.getMessage(),
              {
                afterClose: () => {
                  window.close();
                },
              }
            );
            break;
          default:
            break;
        }
      }
    }
    return throwError(error);
  }

  startReadyToTimeOutTimer(accessToken: string, refreshToken: string) {
    this.lastedActiveTimeStamp = new Date().getTime();
    const MILLISECONDS_PER_SECOND = 1000;
    const MILLISECONDS_PER_MINUTE = 60 * MILLISECONDS_PER_SECOND;

    if (this.timeoutAlertTimerSubc) {
      this.timeoutAlertTimerSubc.unsubscribe();
      delete this.timeoutAlertTimerSubc;
    }
    if (this.timeoutAlert2TimerSubc) {
      this.timeoutAlert2TimerSubc.unsubscribe();
      delete this.timeoutAlert2TimerSubc;
    }
    if (this.timeoutTimerSubc) {
      this.timeoutTimerSubc.unsubscribe();
      delete this.timeoutTimerSubc;
    }

    const jwtPayload = parseJWTtoken(accessToken);
    const iatTime = jwtPayload['iat'] * MILLISECONDS_PER_SECOND;
    const expTime = jwtPayload['exp'] * MILLISECONDS_PER_SECOND;

    const jwtPayload2 = parseJWTtoken(refreshToken);
    const refreshExpTime = jwtPayload2['exp'] * MILLISECONDS_PER_SECOND;

    // 到期五分鐘後還有操作畫面要自動更新token
    let alertTime = expTime + 5 * MILLISECONDS_PER_MINUTE;
    if (refreshExpTime - expTime > 2 * MILLISECONDS_PER_MINUTE) {
      // accessToken 和 refreshToken 的到期時間差必須超過 2 分鐘，不然無法等到超時候才去判斷最後行為需不需要重刷連線
      if (alertTime > (refreshExpTime - MILLISECONDS_PER_MINUTE)) {
        alertTime = (expTime + refreshExpTime) / 2;
      }
    } else {
      alertTime = expTime;
    }
    let alertTimeDiff = alertTime - new Date().getTime();
    if (alertTimeDiff > 0) {
      this.timeoutAlertTimerSubc = timer(alertTimeDiff)
      .subscribe(timeStamp => {
        // 最後的操作在連線有效時間前五分鐘以前
        if (DateTime.fromMillis(this.lastedActiveTimeStamp) <= DateTime.fromMillis(expTime).minus({ minute: 10 })) {
          this.globalComponentService.openSanckBar('還在編輯嗎? 別忘了存檔喔!');
        } else { // 最後的操作在連線有效時間前五分鐘以內就自動更新 token
          this.requestUpdateToken();
        }
      });
    }

    // refreshToken 到期前一分鐘決定要不要更新連線，或強烈提醒第二次
    const alertTime2 = refreshExpTime - MILLISECONDS_PER_MINUTE;
    const timeOutAlert2Diff = alertTime2 - new Date().getTime();
    if (timeOutAlert2Diff > 0) {
      this.timeoutAlert2TimerSubc = timer(timeOutAlert2Diff)
      .subscribe(timeStamp => {
        // 最後的操作在連線有效時間前五分鐘以前
        if (DateTime.fromMillis(this.lastedActiveTimeStamp) <= DateTime.fromMillis(expTime).minus({ minute: 10 })) {
          // this.globalComponentService.openSanckBar('連線已斷開，您已經太久沒操作畫面。<br>請重新開啟編輯頁面');
          this.globalComponentService.openInfoAlert('連線將於 1 分鐘內到期，請盡速存檔!');
        } else { // 最後的操作在連線有效時間前五分鐘以內就自動更新 token
          this.requestUpdateToken();
        }
      });
    }

    // refreshToken 到期後彈出提醒請他關掉視窗
    const untilExpTimeDiff = refreshExpTime - new Date().getTime();
    if (untilExpTimeDiff > 0) {
      this.timeoutTimerSubc = timer(untilExpTimeDiff)
      .subscribe(timeStamp => {
        // 最後的操作在連線有效時間前五分鐘以前
        if (DateTime.fromMillis(this.lastedActiveTimeStamp) <= DateTime.fromMillis(expTime).minus({ minute: 10 })) {
          (this.globalComponentService.globalCmp['infoAlert'] as ReusableInfoAlertComponent).infoConfirm.close();
          this.globalComponentService.openInfoAlert('連線已斷開，您已經太久沒操作畫面。<br>請重新開啟編輯頁面');
        } else { // 最後的操作在連線有效時間前五分鐘以內就自動更新 token
          this.requestUpdateToken();
        }
      });
    }
  }

  /** 要求維持連線 */
  keepConnection() {
    this.lastedActiveTimeStamp = new Date().getTime();
  }

  /** 要求更新存取權杖 */
  requestUpdateToken() {
    const stageUrl = decodeURIComponent(atob(decodeURIComponent(this.route.snapshot.queryParams['stage'])));
    window.opener.postMessage('timeoutRefresh', stageUrl);
  }
}
